/* CSS */
import "@js/parts/css";

/**
 * Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log("HMR");
    });
}

// App imports
import barba from '@barba/core';
import NavigationScroll from '@js/modules/NavigationScroll';
import FloorplanDatePicker from '@js/modules/FloorplanDatePicker';
import gsap from 'gsap';
import htmx from 'htmx.org';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';

window.htmx = htmx;

// Sprig's dev-mode callbacks also run after navigating from pages without components.
window.Sprig = window.Sprig || { components: [] };

// Project imports
// Project utils
import env from '@js/utils/env';
import utils from '@js/utils/utils';

// Project helpers
import Calcs from '@js/helpers/Calcs';
import DelayedCall from '@js/helpers/DelayedCall';
import Url from '@js/helpers/Url';

// Project modules
import Appear from '@js/modules/Appear';
import AppearGrid from '@js/modules/AppearGrid';
import AppearList from '@js/modules/AppearList';
import ExternalLinks from '@js/modules/ExternalLinks';
import Navigation from '@js/modules/Navigation';
import Page from '@js/modules/Page';
import PageScrolling from '@js/modules/PageScrolling';
import PageScrollingManager from '@js/modules/PageScrollingManager';
import ProgressLine from '@js/modules/ProgressLine';
import ScrollTo from '@js/modules/ScrollTo';
import Slideover from '@js/modules/Slideover';

// Alpine
window.Alpine = Alpine;
Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.start();

// HTML element classlist
const HTML_CLASSLIST = document.documentElement.classList;
const BODY = document.body;
const BODY_CLASSLIST = BODY.classList;
const PAGE_TRANSITION_SPEED = 0.7;

window.APP =
    window.APP ||
    new (function APP() {
        // Utilities
        this.env = env;
        this.utils = utils;

        // Helpers
        this.helpers = {
            DelayedCall: DelayedCall,
            ModalOpenClass: '_modal-open',
            PreviousUrl: null,
            ShowSlideover: false,
            Url: Url,
        };

        this.modules = {
            PageScrolling: PageScrolling,
            PageScrollingManager: PageScrollingManager,
        };

        // Window Load
        window.addEventListener('load', e => {
            PageScrollingManager.update(true);
        });

        // Pages
        this.pages = {};

        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }

        const navigationScroll = new NavigationScroll(window);

        let currentController;
        let currentClasses;
        let currentNameSpace;

        const beforeController = (data, done) => {
            console.log('beforeController');
            navigationScroll.before(data.trigger);

            // Close floor plan slide panel if open
            const floorPlanSlidePanel = document.querySelector('.floor-plans-slide-panel');
            if (floorPlanSlidePanel && floorPlanSlidePanel.classList.contains('open')) {
                floorPlanSlidePanel.classList.remove('open');
            }

            done();
        };

        const afterLeaveController = (data, done) => {
            console.log('afterLeaveController');
            done();
        };

        const leaveController = (container, done) => {
            HTML_CLASSLIST.add('_page-loading');
            HTML_CLASSLIST.remove('_page-loaded');

            // Stop scroll
            PageScrolling.lock(document.body);

            // Show progress line
            ProgressLine.show();

            // Fade out container
            gsap.to(container, {
                duration: PAGE_TRANSITION_SPEED,
                alpha: 0,
            });

            // Clear appear animations
            AppearGrid.destroy();
            AppearList.destroy();
            FloorplanDatePicker.destroy();

            // Leave the current page if exists
            currentController &&
            currentController.leave &&
            currentController.leave();

            done();
        };

        const enterController = (container, firstCall = false) => {
            // Unlock first: mobile scroll locks restore the outgoing page's position.
            PageScrolling.unlock(document.body);
            navigationScroll.apply();

            // Modules
            ScrollTo.build();

            if (!firstCall) {
                ProgressLine.hide();
            }

            // Appear animations
            Appear.init();
            AppearGrid.init();
            AppearList.init();

            PageScrolling.start();
            PageScrollingManager.update(true);

            // Processes new content, enabling htmx behavior
            if (!firstCall) {
                htmx.process(document.body);
            }

            // Vars
            const nameSpace = container.getAttribute('data-spa-namespace');
            let headerOverlay = container.getAttribute('data-spa-header-overlay');
            let hideHeaderLogo = container.getAttribute('data-spa-hide-header-logo');
            let bgFade = container.getAttribute('data-spa-bg-fade');
            let theme = container.getAttribute('data-spa-theme');

            const HEADER = document.querySelector('.mn-hdr');
            const HEADER_LOGO = document.querySelector('.hdr-logo');
            if (HEADER) {
                const HEADER_CLASSLIST = HEADER.classList;

                // The header persists between pages; refresh its hero text.
                const heroEyebrow = HEADER.querySelector('.hero-eyebrow');
                const heroEyebrowContent = container.querySelector('template[data-hero-eyebrow]');
                if (heroEyebrow) {
                    heroEyebrow.innerHTML = heroEyebrowContent?.innerHTML || '';
                    heroEyebrow.hidden = !HEADER.querySelector('.nav-btn') || !heroEyebrow.textContent.trim();
                    HEADER_CLASSLIST.toggle('has-hero-eyebrow', !heroEyebrow.hidden);
                }

                // Header overlay
                if (headerOverlay === 'true') {
                    HEADER_CLASSLIST.add('_overlay');
                } else {
                    HEADER_CLASSLIST.remove('_overlay');
                }

                // Remove style attr
                HEADER.removeAttribute('style');
                HEADER_LOGO.removeAttribute('style');

                // Hide logo from header
                if (hideHeaderLogo === 'true') {
                    HEADER_CLASSLIST.add('hide-logo');
                } else {
                    HEADER_CLASSLIST.remove('hide-logo');
                }

                // Update BG fade
                if (bgFade) {
                    HEADER.setAttribute('data-bg-fade', bgFade);
                }
                else {
                    HEADER.removeAttribute('data-bg-fade');
                }
            }

            // Page theme
            function removeThemeClass(element) {
                element.classList.forEach(className => {
                    if (/^theme\d+$/.test(className)) {
                        element.classList.remove(className);
                    }
                });
            }
            // Remove theme class from body element
            removeThemeClass(BODY);
            // Add new theme class to the body element
            BODY_CLASSLIST.add(theme);

            // Page controller
            const pageController = this.pages[nameSpace.toLowerCase()];
            if (pageController) {
                // Run page JS
                currentController = new pageController(container);
            }
            else {
                currentController = null;
            }

            // Test for old classes - if any, remove them
            if (currentClasses) {
                currentClasses.forEach(cssClass => {
                    HTML_CLASSLIST.remove(cssClass);
                });
            }
            // Get new classes - if any, add them
            currentClasses = (container.getAttribute('data-spa-class') || null);
            if (currentClasses) {
                currentClasses = currentClasses.split(' ');

                currentClasses.forEach(cssClass => {
                    HTML_CLASSLIST.add(cssClass);
                });
            }

            currentNameSpace && HTML_CLASSLIST.remove(currentNameSpace);
            currentNameSpace = '_page-' + nameSpace;
            HTML_CLASSLIST.add(currentNameSpace);

            HTML_CLASSLIST.remove('_page-loading');
            HTML_CLASSLIST.add('_page-loaded');

            // Update active links on page
            // Supports sections pages under sections
            Array.from(document.querySelectorAll('a:not(.exclude-active)')).forEach((link) => {
                if (
                    this.utils.testHrefIsActual(link.getAttribute('href'))
                    || window.location.href.includes(link.href)
                ) {
                    link.classList.add('_active');
                }
                else {
                    link.classList.remove('_active');
                }
            });

            // Update active state on nav buttons
            Array.from(document.querySelectorAll('.nav-btn')).forEach((btn) => {
                let dataHref = btn.getAttribute('data-href');

                if (
                    this.utils.testHrefIsActual(dataHref)
                    || window.location.href.includes(dataHref)
                ) {
                    btn.classList.add('_active');
                }
                else {
                    btn.classList.remove('_active');
                }
            });

            // Mark external links
            this.helpers.DelayedCall.create(1000, () => {
                ExternalLinks.build();
            });

            if (!firstCall) {
                gsap.from(container, {
                    duration: PAGE_TRANSITION_SPEED,
                    alpha: 0
                });
            }
        };

        const afterController = (data) => {
            // Apply again after Barba removes the outgoing container and layout settles.
            navigationScroll.apply();
            PageScrolling.update();
            PageScrollingManager.update(true);

            window.APP.helpers.PreviousUrl = barba.history.previous?.url ?? null;

            // Reinit page functionality
            Page.init();
        };

        const start = () => {
            HTML_CLASSLIST.remove('_page-loading');

            enterController(document.body.querySelector('[data-spa-namespace]'), true);

            HTML_CLASSLIST.add('_page-loaded');

            Page.init();

            // Escape key press
            document.onkeydown = (e) => {
                // Escape key maps to keycode `27`
                if (e.key === 'Escape') {
                    if (Alpine.store('nav').isOpen == true) {
                        Alpine.store('nav').close();
                    }
                }
            };

            /**
             * Init Barba
             */
            barba.init({
                schema: {
                    prefix: 'data-spa',
                },
                sync: true,
                debug: false,
                prefetchIgnore: true, // Don't prefetch on hover, it causes issues with slideovers
                cacheIgnore: true,
                preventRunning: true,
                requestError: (trigger, action, url, response) => {
                    // Go to a custom 404 page if the user click on a link that return a 404 response status
                    if (action === 'click' && response.status && response.status === 404) {
                        barba.go('/404');
                    }

                    // Prevent Barba from redirecting the user to the requested URL
                    // this is equivalent to e.preventDefault() in this context
                    return false;
                },
                prevent: ({ el, event }) => {
                    event.stopPropagation();

                    // External links
                    if (
                        el.getAttribute('data-external') !== null ||
                        el.getAttribute('target') === '_blank' ||
                        el.getAttribute('type') === 'submit'
                    ) {
                        console.log('External link');
                        return true;
                    }

                    // Internal anchor links
                    if (el.getAttribute('href').startsWith('#')) {
                        console.log('Anchor link');
                        return true;
                    }

                    // Handle Slideover Links
                    if (el.hasAttribute('data-slideover')) {
                        console.log('Slideover link detected');
                        event.preventDefault();

                        const slideoverData = el.getAttribute('data-slideover').split(',');
                        const url = slideoverData[0];
                        const type = slideoverData[1];

                        Slideover.open(type);

                        // Push state so back button works
                        barba.history.add(url, 'barba', 'push', { slideoverOpen: true, slideoverType: type });

                        console.log('HISTORY');
                        console.log(barba.history);
                        console.log(barba.history.current);
                        console.log(barba.history.previous);

                        return true; // Prevent Barba from handling this click
                    }

                    event.preventDefault();
                    return false; // Allow Barba to handle internal page links
                },
                timeout: 30 * 1000,
                transitions: [{
                    async before(data) {
                        console.log('BEFORE');
                        const done = this.async();

                        beforeController(data, done);
                    },
                    async leave(data) {
                        console.log('LEAVE');
                        const done = this.async();

                        leaveController(data.current.container, done);
                    },
                    async afterLeave(data) {
                        console.log('AFTERLEAVE');
                        const done = this.async();

                        afterLeaveController(data, done);
                    },
                    async enter({ data, current, next }) {
                        console.log('ENTER');
                        console.log({data});

                        console.log('HISTORY');
                        console.log(barba.history);
                        console.log(barba.history.previous?.url);

                        enterController(next.container);
                    },
                    async after({ data }) {
                        console.log({data});
                        console.log('AFTER');

                        afterController(data);

                        // GA page view tracking
                        if (typeof gtag === 'function') {
                            gtag('event', 'page_view');
                        }
                    }
                }]
            });
            navigationScroll.init();
        };


        // Window Load
        window.addEventListener('load', e => {
            PageScrollingManager.update(true);
        });

        // DOM load
        document.addEventListener('DOMContentLoaded', e => {
            // Start SPA
            start();

            // Show nav element - To stop nav flashing at start
            document.querySelector('.nav')?.classList.remove('hidden');

            // Test for HTMX
            if (typeof htmx !== 'undefined') {
                // Reinit search after swap
                htmx.on('htmx:afterSettle', (event) => {
                    Page.init();
                });
            }
        });

        // Handle Popstate (Back & Forward)
        window.addEventListener('popstate', (event) => {
            console.log('POP state');

            if (window.APP.helpers.ShowSlideover) {
                // This history change only closes the overlay; do not start a page transition.
                event.stopImmediatePropagation();
                Slideover.close(); // Close slideover on back
                barba.history.add(barba.history.previous.url, 'barba', 'push', { slideoverOpen: false });

                // Enable scrolling
                PageScrolling.clearLocks();
                navigationScroll.init();

                return false;
            }
            // Barba handles ordinary Back/Forward events and supplies the destination.
        });
    })();
